import React from "react"
import { Provider } from "react-redux"
import { Route, Router, Switch } from "react-router-dom"
import { IntlProvider } from "react-intl-redux"
import moment from "moment"
import MomentUtils from "@date-io/moment"
import { SnackbarProvider } from "notistack"

// region Material Ui
import { ThemeProvider } from "@material-ui/styles"
import CssBaseline from "@material-ui/core/CssBaseline"
import { Box } from "@material-ui/core"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"
// endregion
// region ##   C O M P O N E N T s
import { ConfirmDialog } from "./component/dialog"
import { Notifier, style as useNotifierStyle } from "./component/notifier"
import AppFooter from "./component/AppFooter"
import Header from "./component/header/Header"
import StandaloneWidget from "./component/widgets/StandaloneWidget"
// endregion
// region ##   T H E M I N G  /  C S S  /  U T I L S
import { appTheme as theme } from "./styles"
import "./styles/main.css"
import "./styles/css/font-awesome/css/font-awesome.min.css"
import "./styles/css/widgets/bookmark.css"
import "./dateUtils"
import appStore from "./appStore"
import history from "./History"
import AppRouter from "./AppRouter"
// endregion

// Dangerous section: this import is really important for some specific customer use case
// Template are registered dynamically into TemplateManager (aka components/widgets/TemplateManager)
// By default it is a no operation
// If customer has specific plugin developed then they will be registered
import "./templates"
import RegisterHighchartsPlugin from "./component/widgets/chart/RegisterHighchartsPlugin"
import RegisterHighchartsWidgetPlugin from "./component/widgets/chart/RegisterHighchartsWidgetPlugin"

import "iframe-resizer"
import WebSiteMetaProvider from "./hooks/WebSiteMetaProvider"
import InitIntlInstance from "./i18n/InitIntlInstance"
import SessionCheck from "./SessionCheck"

/**
 * This is the whole webportal application
 * @returns {JSX.Element}
 * @constructor
 */
const MainWebportalApp = () => {
  const classes = useNotifierStyle()
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider
        maxSnack={4}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        classes={classes}
        preventDuplicate
      >
        <SessionCheck>
          <Box display="flex" flexDirection="column" minHeight="100vh">
            <Header />

            {/* Everything BUT footer and header */}
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <RegisterHighchartsPlugin />
              <CssBaseline />
              <Notifier />
              <AppRouter />
              <ConfirmDialog />
            </Box>

            <AppFooter />
          </Box>
        </SessionCheck>
      </SnackbarProvider>
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <Provider store={appStore}>
      <WebSiteMetaProvider>
        <IntlProvider>
          <MuiPickersUtilsProvider
            libInstance={moment}
            utils={MomentUtils}
            locale="fr"
          >
            <Router history={history}>
              <InitIntlInstance />
              <Switch>
                {/*
                  Here we especially DO NOT display the whole website
                  It's a special route to render widget in an iframe from a third party website
                */}
                <Route path="/widget">
                  <RegisterHighchartsWidgetPlugin />
                  <StandaloneWidget />
                </Route>
                <Route component={MainWebportalApp} />
              </Switch>
            </Router>
          </MuiPickersUtilsProvider>
        </IntlProvider>
      </WebSiteMetaProvider>
    </Provider>
  )
}
